import { SetStateAction, useMemo } from 'react';
import { useLocalStorageState } from '@msrceng/component';
import type { IOrderBy, GroupByOption } from 'component/Search/types';
import {
  useStringToDateStateShim,
  DateActionParam,
} from './useStringToDateStateShim';
import { endOfDay, startOfMonth, subMonths } from 'date-fns';

/**
 * Create the state for the filter set shared among all filers.
 */
export const useCommonFilterState = (
  /**
   * The name of the current table (for state context).
   */
  tableName: string,
  /**
   * allows setting of the initial value if the user clears cookies.
   */
  defaultOrder: IOrderBy = { key: 'releaseDate', ascending: false },
  /**
   * allows setting of the initial value if the user clears cookies.
   */
  defaultGroup = 'releaseDate',
  /**
   * The list of items to group by.
   */
  groupByOptionList: GroupByOption[] = []
): IFilterCommonState => {
  const [storageStartDate, setStorageStartDate, resetStartDate] =
    useLocalStorageState<string | undefined>(
      'startDate',
      startOfMonth(subMonths(new Date(), 1)).toISOString()
    );
  const [storageEndDate, setStorageEndDate, resetEndDate] =
    useLocalStorageState<string | undefined>(
      'endDate',
      endOfDay(new Date()).toISOString()
    );
  const [groupName, setGroupBy, resetGroupBy] = useLocalStorageState<string>(
    `${tableName}-groupBy`,
    defaultGroup
  );
  const [orderBy, setOrderBy, resetOrderBy] = useLocalStorageState<
    IOrderBy | undefined
  >(`${tableName}-orderBy`, defaultOrder);
  const [filterText, setFilterText, resetFilterText] = useLocalStorageState(
    `${tableName}-filterText`,
    ''
  );

  const { startDate, setStartDate, endDate, setEndDate } =
    useStringToDateStateShim({
      storageEndDate,
      storageStartDate,
      setStorageEndDate,
      setStorageStartDate,
    });

  const setOrderByFromKey = key => {
    if (orderBy?.key === key) {
      setOrderBy({ key, ascending: !orderBy?.ascending });
    } else {
      setOrderBy({ key, ascending: false });
    }
  };

  const groupBy = useMemo(() => {
    return groupByOptionList?.find(w => w.key === groupName);
  }, [groupName, groupByOptionList]);

  return {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    groupName,
    groupBy,
    setGroupBy,
    filterText,
    setFilterText,
    orderBy,
    setOrderBy: setOrderByFromKey,
    resetStartDate,
    resetEndDate,
    resetGroupBy,
    resetOrderBy,
    resetFilterText,
  };
};

export interface IFilterCommonState {
  startDate: Date | undefined;
  endDate: Date | undefined;
  filterText: string | undefined;
  groupName: string | undefined;
  groupBy: GroupByOption | undefined;
  orderBy: IOrderBy | undefined;
  setStartDate: (T: DateActionParam) => void;
  setEndDate: (T: DateActionParam) => void;
  setFilterText: (T: SetStateAction<string>) => void;
  setGroupBy: (T: SetStateAction<string>) => void;
  setOrderBy: (T: SetStateAction<string | undefined>) => void;
  resetStartDate: () => void;
  resetEndDate: () => void;
  resetGroupBy: () => void;
  resetOrderBy: () => void;
  resetFilterText: () => void;
}
